import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse, ApiError as ApiErrorType } from '@bakedshift/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  // SECURITY: Send HttpOnly cookies with every request
  // Tokens are stored in secure cookies, not localStorage (XSS-safe)
  withCredentials: true,
});

export const WORKSPACE_HEADER = 'x-workspace-id';

// Workspace ID is set dynamically from WorkspaceContext
let currentWorkspaceId: string | null = null;

export function setWorkspaceId(workspaceId: string | null) {
  currentWorkspaceId = workspaceId;
}

/**
 * Name the workspace a single request is about, overriding the module-level
 * one.
 *
 * `currentWorkspaceId` is synced from a React effect, so it lags a workspace
 * state change by one commit: code that calls `setWorkspace(next)` and then
 * awaits a request in the same tick sends that request under the PREVIOUS
 * workspace. That is how the location switcher came to hold a location of
 * another workspace in production. A caller that already knows which
 * workspace it is asking about should say so here rather than depend on when
 * the effect happens to run.
 */
export function withWorkspace(
  workspaceId: string,
  config?: AxiosRequestConfig
): AxiosRequestConfig {
  return {
    ...config,
    headers: { ...config?.headers, [WORKSPACE_HEADER]: workspaceId },
  };
}

/**
 * Request interceptor: fill in the workspace header from the module-level id,
 * but never overwrite one the request named for itself — otherwise
 * `withWorkspace()` above would be silently undone by the very staleness it
 * exists to route around.
 *
 * Exported so the precedence can be tested as the function that is actually
 * registered, instead of reaching into `interceptors.request.handlers`.
 */
export function applyWorkspaceHeader(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  // SECURITY: Tokens are now sent via HttpOnly cookies (set by backend)
  // No Authorization header needed - withCredentials handles cookie transport
  // Include workspace ID header for multi-tenant context
  if (config.headers.has(WORKSPACE_HEADER)) {
    return config;
  }
  if (currentWorkspaceId) {
    config.headers.set(WORKSPACE_HEADER, currentWorkspaceId);
  }
  return config;
}

apiClient.interceptors.request.use(
  applyWorkspaceHeader,
  (error) => Promise.reject(error)
);

// Transient-failure retry for idempotent requests. A cold/warming backend (e.g.
// right after a deploy) answers with a network error or 5xx for the first few
// requests; rather than gate startup on a fixed delay, retry GET/HEAD with
// exponential backoff. Non-idempotent methods and genuine 4xx are never retried.
const RETRYABLE_METHODS = new Set(['get', 'head']);
const MAX_RETRIES = 3;

export function isTransientError(error: AxiosError): boolean {
  // No response → network error or timeout. 5xx → backend not ready yet.
  if (!error.response) return true;
  return error.response.status >= 500;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorType>) => {
    const config = error.config as
      | (AxiosRequestConfig & { _retryCount?: number })
      | undefined;

    if (
      config &&
      RETRYABLE_METHODS.has((config.method ?? '').toLowerCase()) &&
      isTransientError(error)
    ) {
      const attempt = config._retryCount ?? 0;
      if (attempt < MAX_RETRIES) {
        config._retryCount = attempt + 1;
        // Exponential backoff capped at 3.2s: 0.4s, 0.8s, 1.6s
        const delay = Math.min(400 * 2 ** attempt, 3200);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return apiClient(config);
      }
    }

    // 401 handling deliberately lives in AuthContext, not here: it registers a
    // second response interceptor (later, so it receives this rejection) that
    // attempts a refresh and falls back to a hard redirect to /login.
    //
    // This block used to dispatch a CustomEvent('auth:unauthorized') that
    // nothing in the app listened for. It read as the redirect mechanism —
    // Transactions.tsx even logged "redirecting to login" on the strength of it
    // — while doing nothing at all. Removed rather than left as a decoy.
    return Promise.reject(error);
  }
);

export async function apiGet<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.get<ApiResponse<T>>(url, config);
  return response.data;
}

export async function apiPost<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.post<ApiResponse<T>>(url, data, config);
  return response.data;
}

export async function apiPut<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.put<ApiResponse<T>>(url, data, config);
  return response.data;
}

export async function apiPatch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.patch<ApiResponse<T>>(url, data, config);
  return response.data;
}

export async function apiDelete<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.delete<ApiResponse<T>>(url, config);
  return response.data;
}

export async function uploadFile<T>(
  url: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<ApiResponse<T>> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<ApiResponse<T>>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total && onProgress) {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percent);
      }
    },
  });

  return response.data;
}

export function isApiError(error: unknown): error is AxiosError<ApiErrorType> {
  return axios.isAxiosError(error);
}

// The reader's words for an error live in their own module (uiv2 7e) — re-exported
// here, where 60+ call sites already import them.
export { getErrorCode, getErrorMessage, ServerError, ShownError, wordErrorCode } from './api-error';
